// 신규 업체(테넌트) 온보딩 — 업체 정보 입력 → createOrg 호출 → 재로그인 안내
import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { useAuth } from "@/store/auth";

import { plainError } from "@/lib/plainError";
import { digitsOnly, isOwnerPhone, isGuardianContactPhone } from "@/lib/orgPhone";
import { DPA_URL, DPA_VERSION } from "@/lib/dpa";
const SIDO_LIST = [
  "서울특별시", "부산광역시", "대구광역시", "인천광역시", "광주광역시", "대전광역시", "울산광역시",
  "세종특별자치시", "경기도", "강원특별자치도", "충청북도", "충청남도", "전북특별자치도",
  "전라남도", "경상북도", "경상남도", "제주특별자치도",
];

export default function Onboarding() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [sido, setSido] = useState("");
  const [sigungu, setSigungu] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [guardianPhone, setGuardianPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);
  const [isOwner, setIsOwner] = useState(false); // 기사가 잘못 눌러 사장이 되는 것을 막는 확인 칸(3차 점검 11-#9)
  const [dpaAgreed, setDpaAgreed] = useState(false); // 개인정보 처리위탁 계약 동의(필수, F3) — 서버가 판·시각·계정을 적는다

  const submit = async () => {
    if (busy) return;
    if (!isOwner) { setMsg("업체 대표(사장님) 확인 칸에 체크해 주세요. 기사님은 이 화면이 아니라 사장님이 보낸 초대 링크로 들어옵니다."); return; }
    if (!name.trim() || !sido) { setMsg("업체명과 담당 시·도를 넣어 주세요."); return; }
    if (!isOwnerPhone(ownerPhone)) { setMsg("사장님 휴대폰 번호를 010으로 시작하는 숫자로 넣어 주세요."); return; }
    if (!isGuardianContactPhone(guardianPhone)) { setMsg("보호자 문의 번호를 지역번호부터 넣어 주세요(예: 051-123-4567)."); return; }
    if (!dpaAgreed) { setMsg("개인정보 처리위탁 계약서를 읽고 동의 칸에 체크해 주세요. 동의해야 학생 명단을 맡길 수 있습니다."); return; }
    setBusy(true);
    setMsg("업체를 만드는 중…");
    try {
      const fn = httpsCallable(functions, "createOrg");
      const res: any = await fn({
        name: name.trim(),
        sido,
        sigungu: sigungu.split(/[,\uFF0C\u3001\s]+/).map((s) => s.trim()).filter(Boolean),
        warehouseAddress: warehouse.trim(),
        ownerPhone: digitsOnly(ownerPhone),
        guardianContactPhone: digitsOnly(guardianPhone),
        dpaAgreed: true,
        dpaVersion: DPA_VERSION,
      });
      setDone(true);
      setMsg(
        `✅ "${name.trim()}" 업체가 만들어졌습니다.` +
        (res.data?.warehouseOk ? "" : warehouse.trim() ? " (창고 주소 지도 위치를 못 찾음 — 환경 설정에서 다시 지정하세요)" : ""),
      );
    } catch (e: any) {
      setMsg(`❌ 업체를 만들지 못했습니다 — ${plainError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-milk-50 px-4 py-8">
      <div className="card p-8 w-full max-w-lg space-y-4">
        <div>
          <h1 className="text-xl font-extrabold text-milk-800">🏢 새 업체 시작하기</h1>
          <p className="text-sm text-slate-500 mt-1">
            무상우유 배송 관리를 시작합니다. 로그인 계정: <b>{user?.email}</b>
          </p>
        </div>

        {done ? (
          <div className="space-y-4">
            <div className="rounded-xl bg-emerald-50 text-emerald-700 p-4 text-sm">{msg}</div>
            <button
              className="btn-primary w-full"
              onClick={async () => { await user?.getIdToken(true); window.location.replace("/admin"); }}
            >
              관리자 화면으로 시작하기
            </button>
          </div>
        ) : (
          <>
            <label className="block text-sm">
              <span className="font-semibold">업체명 *</span>
              <input className="input mt-1 w-full" placeholder="예: 대구우유유통" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="block text-sm">
              <span className="font-semibold">담당 시·도 *</span>
              <select className="input mt-1 w-full" value={sido} onChange={(e) => setSido(e.target.value)}>
                <option value="">선택하세요</option>
                {SIDO_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {/* 여러 시·도 고르기는 아직 개발 전(대표 결정 Q24) — 그 전까지는 전화 상담으로 받는다 */}
              <span className="text-sm text-slate-600">
                지금은 시·도 하나만 고를 수 있습니다. 두 시·도에 걸쳐 배송하시면(예: 대구·경산) 업체를 만들기 전에{" "}
                <a className="underline font-semibold" href="tel:01026085099">010-2608-5099</a>로 먼저 전화 상담해 주세요.
              </span>
            </label>
            <label className="block text-sm">
              <span className="font-semibold">담당 시·군·구</span>
              <input className="input mt-1 w-full" placeholder="쉼표나 띄어쓰기로 구분 (예: 달서구, 수성구, 달성군)" value={sigungu} onChange={(e) => setSigungu(e.target.value)} />
              <span className="text-sm text-slate-600">적어 두면 담당 밖 주소를 「택배」로 걸러 줍니다. 나중에 환경 설정에서 바꿀 수 있습니다.</span>
            </label>
            <label className="block text-sm">
              <span className="font-semibold">창고(출발지) 주소</span>
              <input className="input mt-1 w-full" placeholder="예: 대구광역시 달서구 성서공단로 11" value={warehouse} onChange={(e) => setWarehouse(e.target.value)} />
              <span className="text-sm text-slate-600">배송 경로의 출발점이 됩니다.</span>
            </label>
            <label className="block text-sm">
              <span className="font-semibold">사장님 휴대폰 *</span>
              <input className="input mt-1 w-full" placeholder="예: 010-1234-5678" value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} inputMode="tel" autoComplete="tel" />
              <span className="text-sm text-slate-600">이용료 청구 문자·긴급 연락·기사님 연락에 씁니다. 보호자에게는 보이지 않습니다.</span>
            </label>
            <label className="block text-sm">
              <span className="font-semibold">보호자 문의 번호 *</span>
              <input className="input mt-1 w-full" placeholder="예: 051-123-4567 (사무실 번호도 됩니다)" value={guardianPhone} onChange={(e) => setGuardianPhone(e.target.value)} inputMode="tel" />
              <span className="text-sm text-slate-600">보호자가 보는 배송 사진 화면과 문자에 이 번호가 보입니다. 사장님 개인 번호를 보이고 싶지 않으면 사무실 번호를 넣으세요.</span>
            </label>
            <p className="text-sm text-slate-500">보호자 배송 알림은 무상우유콕 대표 번호로 보냅니다. 업체 번호로 바꾸려면 나중에 따로 등록해 드립니다.</p>

            {msg && <div className="text-sm text-slate-600">{msg}</div>}
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer rounded-xl border border-amber-300 bg-amber-50 p-3">
              <input type="checkbox" className="mt-1" checked={isOwner} onChange={(e) => setIsOwner(e.target.checked)} />
              <span>저는 이 업체의 <b>대표(사장님)</b>입니다. 기사님은 여기서 업체를 만들지 말고, 사장님이 보낸 <b>초대 링크</b>로 들어오세요. (잘못 만들면 사장님의 초대를 받을 수 없게 됩니다)</span>
            </label>
            <div className="rounded-xl border border-slate-300 bg-white p-3 space-y-2 text-sm text-slate-700">
              <div className="font-semibold">개인정보 처리위탁 계약</div>
              <p>
                학생·보호자 정보는 사장님 업체의 정보이고, 콕소프트는 맡아서 처리합니다. 법에 따라 맡기는 범위·보호조치·다시 맡기는 곳(구글·Solapi·카카오·Anthropic·토스페이먼츠)·파기 시점을 계약서로 정합니다.{" "}
                <a className="underline font-semibold" href={DPA_URL} target="_blank" rel="noreferrer">계약서 전문 보기</a>
              </p>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={dpaAgreed} onChange={(e) => setDpaAgreed(e.target.checked)} />
                <span>개인정보 처리위탁 계약서(버전 {DPA_VERSION})를 읽었고 동의합니다. <span className="text-slate-500">(필수 — 다시 맡기는 곳에 대한 동의 포함)</span></span>
              </label>
            </div>
            <button className="btn-primary w-full" onClick={submit} disabled={busy || !isOwner || !dpaAgreed}>
              {busy ? "생성 중…" : "업체 만들기"}
            </button>
            <p className="text-sm text-slate-600 text-center">
              업체를 만들면{" "}
              <a className="underline" href="https://freemilkkok.com/terms" target="_blank" rel="noreferrer">이용약관</a>
              에 동의한 것으로 보며, 학생·보호자 정보는{" "}
              <a className="underline" href="https://freemilkkok.com/privacy" target="_blank" rel="noreferrer">개인정보처리방침</a>
              에 따라 처리됩니다. 처리위탁 계약은 위 동의로 맺어지고, 동의한 시각과 계정이 기록됩니다.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
